import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Event } from '../../types';
import { apiService } from '../../services/api';

interface EventsState {
  events: Event[];
  currentEvent: Event | null;
  loading: boolean;
  error: string | null;
}

const initialState: EventsState = {
  events: [],
  currentEvent: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (dayId: number) => {
    return await apiService.getEvents(dayId);
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async ({ dayId, data }: { dayId: number; data: Partial<Event> }) => {
    return await apiService.createEvent(dayId, data);
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, data }: { id: number; data: Partial<Event> }) => {
    return await apiService.updateEvent(id, data);
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (id: number) => {
    await apiService.deleteEvent(id);
    return id;
  }
);

export const reorderEvents = createAsyncThunk(
  'events/reorderEvents',
  async ({ dayId, eventOrders }: { dayId: number; eventOrders: Array<{ event_id: number; order: number }> }) => {
    return await apiService.reorderEvents(dayId, eventOrders);
  }
);

export const createAttachment = createAsyncThunk(
  'events/createAttachment',
  async ({ eventId, data }: { eventId: number; data: FormData }) => {
    const attachment = await apiService.createAttachment(eventId, data);
    return { attachment, eventId };
  }
);

export const deleteAttachment = createAsyncThunk(
  'events/deleteAttachment',
  async (id: number) => {
    await apiService.deleteAttachment(id);
    return id;
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
      state.currentEvent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка загрузки событий';
      })
      // Create event
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
      })
      // Update event
      .addCase(updateEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(event => event.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        if (state.currentEvent?.id === action.payload.id) {
          state.currentEvent = action.payload;
        }
      })
      // Delete event
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter(event => event.id !== action.payload);
        if (state.currentEvent?.id === action.payload) {
          state.currentEvent = null;
        }
      })
      // Reorder events
      .addCase(reorderEvents.fulfilled, (state, action) => {
        state.events = action.payload;
      })
      // Create attachment
      .addCase(createAttachment.fulfilled, (state, action) => {
        const eventIndex = state.events.findIndex(event => event.id === action.payload.eventId);
        if (eventIndex !== -1) {
          state.events[eventIndex].attachments.push(action.payload.attachment);
        }
        if (state.currentEvent?.id === action.payload.eventId) {
          state.currentEvent.attachments.push(action.payload.attachment);
        }
      })
      // Delete attachment
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        state.events.forEach(event => {
          event.attachments = event.attachments.filter(attachment => attachment.id !== action.payload);
        });
        if (state.currentEvent) {
          state.currentEvent.attachments = state.currentEvent.attachments.filter(
            attachment => attachment.id !== action.payload
          );
        }
      });
  },
});

export const { clearError, setCurrentEvent } = eventsSlice.actions;
export default eventsSlice.reducer; 